import { useEffect, useMemo, useState } from 'react';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import useMediaQuery from '@/hooks/useMediaQuery';
import { Reference } from '@/types/lib/reference';
import { Link } from '@/types/link';
import { resolveReference } from '@/utils/lib/resolve-reference';
import { NavigationCategory } from '@/components/organisms/header/types';
import useAccountRoles from '@/lib/hooks/useAccountRoles';
import { bigDesktop, desktop,tablet,mediumDesktop } from '@/constants/screensizes';
import useResizeObserver from '@/hooks/useResizeObserver';
import AnnouncementBar from '@/components/organisms/announcement-bar';
import Header from '@/components/organisms/header';
import { mapProductSuggestion } from '@/utils/mappers/map-product-suggestion';
import { TasticComponentProps } from '../types';
import { HeaderTasticProps } from './types';
import useHeaderData from './hooks/useHeaderData';
import { useParams, useRouter } from 'next/navigation';

const PostLoginHeader = ({ data }: TasticComponentProps<HeaderTasticProps>) => {
  const { locale } = useParams();
  const { isAdmin } = useAccountRoles();
  const { translate } = useTranslation();
  const [ismediumDesktopSize] = useMediaQuery(mediumDesktop);
  const [isDesktopSize] = useMediaQuery(desktop);
  const {
    account,
    businessUnits,
    selectedBusinessUnit,
    stores,
    selectedStore,
    // quotes,
    totalCartItems,
    navigationCategories,
    headerSearch,
    headerProducts,
    quickOrderSearch,
    quickOrderProducts,
    addToCart,
    onHeaderSearch,
    onQuickOrderSearch,
    onBusinessUnitSelect,
    onStoreSelect,
    onLogoutClick,
  } = useHeaderData();
  const country = selectedBusinessUnit?.addresses[0]?.country.toLowerCase();



  const pageLinks: Link[] = data?.data?.dataSource?.navigation_menu?.map(
    (pl: { label: any; call_to_action: { href: Reference | undefined }; mega_menu: any }) => {
      return {
        name: pl?.label,
        href: resolveReference(pl?.call_to_action?.href).href,
        subMenu: pl?.mega_menu?.submenu_links,
      };
    },
  );
  const myAccountMenu: NavigationCategory = {
    categoryId: data?.accountLinkId,
    name: data?.accountLinkLabel,
    path: resolveReference(data.accountLink).href ?? '/',
    subCategories: data.accountPageLinks?.map((link) => {
      return {
        categoryId: link?.linkId,
        name: link.name,
        path: resolveReference(link?.href).href ?? '/',
        subCategories: [],
        paths: {},
        descendants: [],
      };
    }),
    paths: {},
    descendants: [],
  };

  const accountLinks: NavigationCategory[] = useMemo(() => {
    return isAdmin
      ? myAccountMenu?.subCategories
      : myAccountMenu?.subCategories?.filter((subCategory) => subCategory.categoryId != 'company-admin');
  }, [isAdmin, myAccountMenu.subCategories]);

  const accountMenuMobile: NavigationCategory = {
    ...myAccountMenu,
    subCategories: myAccountMenu?.subCategories?.filter((subCategory) => subCategory.categoryId != 'company-admin'),
  };

  const { ref } = useResizeObserver(
    (el: HTMLDivElement) => {
      const bodySection = document.querySelector('.body-section') as HTMLDivElement | undefined;
      if (bodySection) bodySection.style.paddingTop = `${el.clientHeight}px`;
    },
    () => {
      const bodySection = document.querySelector('.body-section') as HTMLDivElement | undefined;
      if (bodySection) bodySection.style.paddingTop = '0';
    },
  );

  return (
    <div id="header-container" className="fixed left-0 right-0 top-0  z-50" ref={ref}>
      <div className="relative bg-white">
        <AnnouncementBar
          isAdmin={isAdmin}
          textBar={data.announcementBarText}
          accountLinks={accountLinks}
          selectedBusinessUnit={selectedBusinessUnit?.key ?? translate('select')}
          businessUnits={businessUnits}
          stores={(stores ?? []).map((st:any) => {
            return { name: st.name ?? st.key, value: st.key };
          })}
          selectedStore={selectedStore?.key ?? translate('select')}
          name={account?.firstName ?? ''}
          // quotes={quotes?.length ?? 0}
          onLogoutClick={onLogoutClick}
          onBusinessUnitChange={onBusinessUnitSelect}
          onStoreChange={onStoreSelect}
          logo={data?.data?.dataSource?.desktop_logo?.url}
          logoLink={resolveReference(data?.logoLink)}
          country={country}
          topRightMenu={data.data.dataSource.top_right_menu}
        />
        <Header
          variant={data.variant ?? 'navigation'}
          isAdmin={isAdmin}
          csvDownloadLink="/template.csv"
          quickOrderProducts={quickOrderProducts.map((product) => mapProductSuggestion(product))}
          selectedBusinessUnit={selectedBusinessUnit?.key ?? ''}
          businessUnits={businessUnits}
          stores={(stores ?? []).map((st:any) => {
            return { name: st.name ?? st.key, value: st.key };
          })}
          selectedStore={selectedStore?.key ?? ''}
          categoryLinks={navigationCategories}
          myAccountMenu={accountMenuMobile}
          accountLink={resolveReference(data.accountLink)}
          cartItems={totalCartItems}
          cartLink={resolveReference(data.cartLink)}
          pageLinks={pageLinks}
          mobileLinks={data?.data?.dataSource?.navigation_menu}
          logo={data?.data?.dataSource?.desktop_logo?.url}
          logoLink={resolveReference(data?.logoLink)}
          searchPlaceholder={isDesktopSize?translate('common.search.placeholder.mobile'):
            ismediumDesktopSize ? translate('common.search.placeholder') : translate('common.search.placeholder.mobile')
          }
          searchSuggestions={headerProducts.map((product) => mapProductSuggestion(product))}
          // quotes={quotes?.length ?? 0}
          onBusinessUnitChange={onBusinessUnitSelect}
          onStoreChange={onStoreSelect}
          quickOrderSearch={quickOrderSearch}
          onQuickOrderSearch={onQuickOrderSearch}
          headerSearch={headerSearch}
          onHeaderSearch={onHeaderSearch}
          addToCart={addToCart}
          categories={navigationCategories}
        />
      </div>
    </div>
  );
};

export default PostLoginHeader;
